import { useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { ThirdPartyApp } from '../../../../../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getThirdPartyApps,
  getUserQuery,
} from '../../../../api/users-api';
import DeleteThirdPartyAppsDialog from './delete-thirdPartyApps-dialog';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Apps from '@mui/icons-material/Apps';

export default function ThirdPartyApps() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();

  const { data: user } = useQuery(getUserQuery(userEmail));

  const queryClient = useQueryClient();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openDeleteThirdPartyApps, setOpenDeleteThirdPartyApps] =
    useState(false);

  const {
    data: thirdPartyApps,
    isLoading: thirdPartyAppsIsLoading,
    isFetching: thirdPartyAppsFetching,
  } = useQuery({
    queryKey: [UserKeys.ThirdPartyApps, userEmail],
    queryFn: () => getThirdPartyApps(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('third party applications'),
    },
  });

  const columns: GridColDef<ThirdPartyApp>[] = [
    {
      field: 'displayText',
      headerName: 'Application Name',
      flex: 1,
      display: 'flex',
    },
    {
      field: 'scopes',
      headerName: 'Permissions Granted',
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        return (
          <Stack gap={0.5} sx={{ my: 0.5, maxHeight: 100, overflow: 'auto' }}>
            {params.value.map((scope: string) => (
              <Chip
                key={scope}
                label={scope}
                size="small"
                sx={{ width: 'fit-content' }}
              />
            ))}
          </Stack>
        );
      },
    },
  ];

  if (!user) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  const selectedApps = thirdPartyApps?.filter((app) => {
    return selectedRows.includes(app.clientId);
  });

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        secondaryButtons={
          <>
            <SharedIconButton
              variant="delete"
              onClick={() => setOpenDeleteThirdPartyApps(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.RevokeAuthTokens.value
                )
              }
              tooltipLabel="Delete"
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.ThirdPartyApps, userEmail],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      />
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Apps', <Apps />),
        }}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.clientId}
        rows={thirdPartyApps || []}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        toolbarQuickFilterWidth={400}
        loading={thirdPartyAppsIsLoading || thirdPartyAppsFetching}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        checkboxSelection
        defaultGridState={{
          sorting: {
            sortModel: [
              {
                field: 'displayText',
                sort: 'asc',
              },
            ],
          },
        }}
      />
      {selectedApps && selectedApps.length > 0 ? (
        <DeleteThirdPartyAppsDialog
          open={openDeleteThirdPartyApps}
          handleClose={() => setOpenDeleteThirdPartyApps(false)}
          selectedApps={selectedApps}
          userEmail={userEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
